import {action, makeObservable, observable, reaction, comparer, runInAction} from "mobx";
import {topic, topicStub, topicUuid, topicUpdates, projectUuid, syncHandler, syncData} from "../model/index.js";
import app from "../app.js";
import {loadObject, getDateString, prepareForCreate, prepareForUpdate, cleanUp} from "../utils/index.js";
import {Sync, outSyncHandler} from "../sync/index.js";
import {Store} from "./index.js";
import {fetchLockOptions} from "./common.js";
import outlineStore from "./outline.js";
import Uuid from "../../utils/uuid.js";
import AsyncLock from "async-lock";

export class TopicStore implements Store {
	_topics:         Record<topicUuid, topic> = {};
	_projectsLoaded: Record<projectUuid, boolean> = {};
	_fetchLock = new AsyncLock(fetchLockOptions);

	constructor() {
		makeObservable(this, {
			create:          action,
			update:          action,
			_topics:         observable,
			_projectsLoaded: observable,
			_clear:          action,
			_clearByProject: action,
			_load:           action,
			_loadMultiple:   action,
			_remove:         action,
			_removeFromSync: action,
			_reorder:        action
		});
	}

	_clear() {
		this._topics = {};
		this._projectsLoaded = {};
	}

	_clearByProject(projectUuid: projectUuid) {
		(Object.keys(this._topics) as topicUuid[])
			.filter(uuid => this._topics[uuid].project_uuid === projectUuid)
			.forEach(this._remove.bind(this));

		this._projectsLoaded[projectUuid] = false;
	}

	_refresh() {
		(Object.keys(this._projectsLoaded) as projectUuid[])
			.filter(uuid => this._projectsLoaded[uuid])
			.forEach(uuid => this._fetchByProject(uuid, true));
	}

	_isProjectLoaded(projectUuid: projectUuid): boolean {
		return this._projectsLoaded[projectUuid];
	}

	byProject(projectUuid: projectUuid): topic[] {
		if (!this._isProjectLoaded(projectUuid)) {
			// noinspection JSIgnoredPromiseFromCall
			this._fetchByProject(projectUuid);
		}

		return Object.values(this._topics)
			.filter(topic => topic.project_uuid === projectUuid)
			.sort((a, b) => a.position - b.position);
	}

	/**
	 * Returns a topic by its UUID. Does not trigger project loading (as it doesn't know the project UUID).
	 */
	byUuid(uuid: topicUuid): topic | undefined {
		return Object.values(this._topics)
			.find(topic => topic.uuid === uuid);
	}

	async _fetchByProject(projectUuid: projectUuid, force = false) {
		await this._fetchLock.acquire("_fetchByProject:" + projectUuid, async () => {
			if (!app.isLoggedIn)
				return;
			if (force || !this._isProjectLoaded(projectUuid)) {
				const topics = await app.backend.getTopics(projectUuid);

				runInAction(() => {
					if (force)
						this._clearByProject(projectUuid);

					this._loadMultiple(topics);
				});
			}
		});
	}

	_load(topic: topic) {
		this._projectsLoaded[topic.project_uuid] = true;
		loadObject(this._topics, topic.uuid, topic);
	}

	_loadMultiple(topics: topic[]) {
		topics.map(this._load.bind(this));
	}

	_remove(uuid: topicUuid) {
		delete this._topics[uuid];
	}

	_reorder(data: syncData[syncHandler.TOPICS_REORDER]) {
		const {project_uuid, topics} = data;
		topics.forEach(t => {
			if (this._topics[t.uuid] === undefined)
				// This topic is not loaded, ignore it
				return;

			if (this._topics[t.uuid].project_uuid !== project_uuid)
				// Mismatch in topic's project UUID (should never happen, but if it does, ignore it)
				return;

			this._topics[t.uuid].position = t.position;
		});
	}

	async unstub(stub: topicStub): Promise<topic> {
		const topic = Object.assign({
			uuid:       Uuid.base64() as topicUuid,
			created_dt: getDateString(),
			updated_dt: null
		}, stub) as topic;

		if (topic.position === undefined) {
			// Make sure we have the topics loaded, otherwise we can't calculate the new position
			await this._fetchByProject(topic.project_uuid);
			const positions = this.byProject(topic.project_uuid).map(t => t.position);
			topic.position = positions.length === 0 ? 1 : 1 + Math.max(...positions);
		}

		return topic;
	}

	async create(data: topicStub, ctx?: any): Promise<topic> {
		const topic = prepareForCreate(await this.unstub(data)) as topic;
		const uuid = topic.uuid;
		this._load(topic);

		// noinspection ES6MissingAwait
		(async () => {
			// Start a process that will call the backend and remove the "_pending" flag from the topic
			// when it successfully returns (or otherwise mark it as errored).
			try {
				await app.backend.createTopic(cleanUp(topic) as topic);
				runInAction(() => {
					if (this._topics[uuid])
						this._topics[uuid] = cleanUp(this._topics[uuid]) as topic;
				});
			} catch (e) {
				app.handleBackendError(e, ctx);
				this._remove(uuid);
			}
		})();

		return topic;
	}

	async update(uuid: topicUuid, updates: topicUpdates, ctx?: any): Promise<topic> {
		if (this._topics[uuid] === undefined)
			throw new Error("Topic not found: " + uuid);

		const original = cleanUp(this._topics[uuid]) as topic;
		const topic = Object.assign(prepareForUpdate(this._topics[uuid]) as topic, updates);
		this._load(topic);

		// noinspection ES6MissingAwait
		(async () => {
			// Start a process that will call the backend and remove the "_pending" flag from the topic
			// when it successfully returns (or otherwise mark it as errored).
			try {
				await app.backend.updateTopic(uuid, updates);
				runInAction(() => {
					if (this._topics[uuid])
						this._topics[uuid] = cleanUp(this._topics[uuid]) as topic;
				});
			} catch (e) {
				app.handleBackendError(e, ctx);
				this._load(original);
			}
		})();

		return topic;
	}

	_setupSyncHandlers(sync: Sync) {
		sync.setHandler(syncHandler.TOPIC_ADD, this._load.bind(this));
		sync.setHandler(syncHandler.TOPICS_ADD, this._loadMultiple.bind(this));

		sync.setHandler(syncHandler.TOPIC_MODIFY, (topic: syncData[syncHandler.TOPIC_MODIFY]) =>
			this._isProjectLoaded(topic.project_uuid) // are topics loaded for this project?
			&& this._load(topic)                      // load it
		);

		sync.setHandler(syncHandler.TOPIC_DELETE, this._removeFromSync.bind(this));

		sync.setHandler(syncHandler.TOPICS_REORDER, this._reorder.bind(this));
	}

	_removeFromSync({project_uuid, topic_uuid}: syncData[syncHandler.TOPIC_DELETE]) {
		this._remove(topic_uuid);
		outlineStore._removeTopic(topic_uuid, project_uuid);
	}

	_setupOutSyncHandlers(handler: outSyncHandler) {
		reaction(() => JSON.parse(JSON.stringify(this._topics)), (current, previous) => {
			if (!app.isLoggedIn)
				return;

			const addMsgs: Record<projectUuid, syncData[syncHandler.TOPICS_ADD]> = {};
			Object.keys(current)
				.filter(uuid => previous[uuid] === undefined)
				.forEach(uuid => {
					const projectUuid = current[uuid].project_uuid;
					if (addMsgs[projectUuid] === undefined)
						addMsgs[projectUuid] = {topics: []};
					addMsgs[projectUuid].topics.push(current[uuid]);
				});
			(Object.keys(addMsgs) as projectUuid[])
				.forEach(projectUuid => handler(projectUuid, syncHandler.TOPICS_ADD, addMsgs[projectUuid]));

			Object.keys(previous)
				.filter(uuid => current[uuid] === undefined)
				.map(uuid => handler(previous[uuid].project_uuid, syncHandler.TOPIC_DELETE, {
					project_uuid: previous[uuid].project_uuid,
					topic_uuid:   uuid
				}));

			Object.keys(current)
				.filter(
					uuid => previous[uuid] !== undefined
						// Ignore `position` fields, we deal with those separately
						&& !comparer.structural(
							Object.assign({}, current[uuid], {position: 0}),
							Object.assign({}, previous[uuid], {position: 0})
						)
				)
				.map(uuid => handler(current[uuid].project_uuid, syncHandler.TOPIC_MODIFY, current[uuid]));
		});

		// This is a data structure suitable for detecting position changes in topics, ignoring other changes (e.g. a
		// topic's color). The inner value is the `position` of the topic.
		type topicReorderData = Record<projectUuid, Record<topicUuid, number>>;
		reaction(
			() => Object.keys(this._topics)
				.reduce((acc: topicReorderData, topicUuid: topicUuid) => {
					const {project_uuid, position} = this._topics[topicUuid];
					if (acc[project_uuid] === undefined)
						acc[project_uuid] = {};
					acc[project_uuid][topicUuid] = position;
					return acc;
				}, {}),
			(current: topicReorderData, previous: topicReorderData) => {
				if (!app.isLoggedIn)
					return;

				(Object.keys(current) as projectUuid[])
					// Only do this when `previous` and `current` have values
					.filter(projectUuid => previous[projectUuid] !== undefined)

					// Build one TOPICS_REORDER message per project, from `current`
					.forEach(projectUuid => handler(projectUuid, syncHandler.TOPICS_REORDER, {
						project_uuid: projectUuid,
						topics:       Object.keys(current[projectUuid])
							.map((topicUuid: topicUuid) => ({
								uuid:     topicUuid,
								position: current[projectUuid][topicUuid]
							}))
					}));
			}
		);
	}
}

export default new TopicStore();
